import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  decideAgreementApprovalStep,
  exportAgreementXlsx,
  getAgreement,
  getAgreementApprovalById,
} from "../../../api/agreementsApi";
import { getAllCounterparties } from "../../../api/api";
import { getProjectEstimate } from "../../../api/projectEstimatesApi";
import { UserContext } from "../../../contexts/CurrentUserContext";
import Preloader from "../../Preloader/Preloader";
import {
  APPROVAL_STATUS_LABELS,
  APPROVAL_STEP_STATUS_LABELS,
} from "../../ProjectsV2/ProjectFinance/Agreements/agreementsListHelpers";
import { resolveUiPerformerTypeFromAgreement } from "../../ProjectsV2/ProjectFinance/Agreements/agreementFormHelpers";
import {
  getAgreementBlocksRows,
  getPaymentSchedulePercentages,
  getPaymentScheduleDescription,
  getPaymentStageTemplate,
  hasPaymentScheduleData,
  PERFORMER_TYPE_LABELS,
} from "../../ProjectsV2/ProjectFinance/AgreementsV2/agreementsHelpers";
import { resolveAgreementCounterpartyName } from "./agreementApprovalHelpers";
import "./AgreementApprovalDetail.css";

const PERFORMER_TYPE_DISPLAY_LABELS = {
  ...PERFORMER_TYPE_LABELS,
  individual_gph: "Физ лицо на ГПХ",
};

const getDirectPerformerTypeLabel = (performerType) => {
  if (!performerType || performerType === "counterparty") return null;
  return (
    PERFORMER_TYPE_DISPLAY_LABELS[performerType] ?? performerType ?? null
  );
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => {
  if (amount == null) return "—";
  return `${new Intl.NumberFormat("ru-RU").format(amount)} ₸`;
};

const buildSectionNameMap = (estimate) => {
  const map = {};
  const addSection = (item) => {
    const sectionId = item?.section?.id;
    const sectionName = item?.section?.name;
    if (sectionId != null && sectionName) {
      map[Number(sectionId)] = sectionName;
    }
  };
  (estimate?.contract_sections?.sections || []).forEach(addSection);
  (estimate?.construction_design || []).forEach(addSection);
  (estimate?.construction_design_project || []).forEach(addSection);
  return map;
};

function AgreementApprovalDetail() {
  const { agreementId, approvalId } = useParams();
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [approval, setApproval] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [sectionNameById, setSectionNameById] = useState({});
  const [performerTypeLabel, setPerformerTypeLabel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [approvalData, agreementData] = await Promise.all([
        getAgreementApprovalById(approvalId),
        getAgreement(agreementId),
      ]);
      setApproval(approvalData);
      setAgreement(agreementData);
    } catch (err) {
      setError(err?.message || "Ошибка при загрузке согласования");
      setApproval(null);
      setAgreement(null);
    } finally {
      setLoading(false);
    }
  }, [agreementId, approvalId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const projectId = agreement?.project_id;
    if (!projectId) {
      setSectionNameById({});
      return undefined;
    }

    let cancelled = false;
    getProjectEstimate(Number(projectId))
      .then((estimate) => {
        if (!cancelled) setSectionNameById(buildSectionNameMap(estimate));
      })
      .catch(() => {
        if (!cancelled) setSectionNameById({});
      });

    return () => {
      cancelled = true;
    };
  }, [agreement?.project_id]);

  useEffect(() => {
    if (!agreement) {
      setPerformerTypeLabel(null);
      return undefined;
    }

    const directLabel = getDirectPerformerTypeLabel(agreement.performer_type);
    if (directLabel) {
      setPerformerTypeLabel(directLabel);
      return undefined;
    }

    let cancelled = false;
    setPerformerTypeLabel(PERFORMER_TYPE_DISPLAY_LABELS.counterparty);

    resolveUiPerformerTypeFromAgreement(agreement, getAllCounterparties)
      .then((uiType) => {
        if (cancelled) return;
        setPerformerTypeLabel(
          PERFORMER_TYPE_DISPLAY_LABELS[uiType] ??
            uiType ??
            PERFORMER_TYPE_DISPLAY_LABELS.counterparty,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setPerformerTypeLabel(PERFORMER_TYPE_DISPLAY_LABELS.counterparty);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agreement]);

  const steps = Array.isArray(approval?.steps) ? approval.steps : [];
  const myStep = steps.find(
    (step) =>
      userData?.id != null &&
      String(step.approver_id) === String(userData.id),
  );
  const canDecide = myStep?.status === "pending" && approval?.status === "pending";

  const counterpartyName =
    resolveAgreementCounterpartyName(agreement) ?? "—";

  const blockRows = useMemo(
    () => (agreement ? getAgreementBlocksRows(agreement) : []),
    [agreement],
  );

  const sectionLabels = useMemo(() => {
    const sectionIds = Array.isArray(agreement?.section_ids)
      ? agreement.section_ids
      : [];
    return sectionIds.map(
      (sectionId) =>
        sectionNameById[Number(sectionId)] || `Раздел #${sectionId}`,
    );
  }, [agreement?.section_ids, sectionNameById]);

  const paymentStages = useMemo(() => {
    if (!agreement || !hasPaymentScheduleData(agreement)) return [];
    const percentages = getPaymentSchedulePercentages(agreement);
    const totalPrice = Number(agreement.price) || 0;
    return percentages.map(({ percent }, index) => {
      if (percent === 0) return null;
      const template = getPaymentStageTemplate(index);
      const amount =
        totalPrice > 0 ? Math.round((totalPrice * percent) / 100) : null;
      return {
        key: template.key,
        label:
          getPaymentScheduleDescription(agreement, index) || template.label,
        percent,
        amount,
      };
    }).filter(Boolean);
  }, [agreement]);

  const handleBack = () => {
    navigate("/approvals/project-approval?tab=agreements");
  };

  const handleApprove = async () => {
    if (!canDecide || !myStep?.id) return;
    setIsProcessing(true);
    setError(null);
    try {
      await decideAgreementApprovalStep(agreementId, myStep.id, {
        status: "signed",
      });
      navigate("/approvals/project-approval?tab=agreements");
    } catch (err) {
      setError(err?.message || "Ошибка при согласовании");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!canDecide || !myStep?.id) return;
    setIsProcessing(true);
    setError(null);
    try {
      await decideAgreementApprovalStep(agreementId, myStep.id, {
        status: "rejected",
        comment: rejectComment.trim() || undefined,
      });
      navigate("/approvals/project-approval?tab=agreements");
    } catch (err) {
      setError(err?.message || "Ошибка при отклонении");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return <Preloader />;

  if (error && !approval) {
    return (
      <div className="agreement-approval-detail">
        <div className="agreement-approval-detail__error">{error}</div>
        <button
          type="button"
          className="agreement-approval-detail__back-btn"
          onClick={handleBack}
        >
          Вернуться к списку
        </button>
      </div>
    );
  }

  return (
    <div className="agreement-approval-detail">
      <div className="agreement-approval-detail__header">
        <button
          type="button"
          className="agreement-approval-detail__back-btn"
          onClick={handleBack}
        >
          ← Назад к списку соглашений
        </button>
        <div className="agreement-approval-detail__title-wrap">
          <h2 className="agreement-approval-detail__title">
            Согласование соглашения{" "}
            {agreement?.number != null ? `№${agreement.number}` : ""}
          </h2>
          <span
            className={`agreement-approval-detail__status agreement-approval-detail__status--${approval?.status ?? "pending"}`}
          >
            {APPROVAL_STATUS_LABELS[approval?.status] ??
              approval?.status ??
              "—"}
          </span>
        </div>
      </div>

      <div className="agreement-approval-detail__card">
        <div className="agreement-approval-detail__grid">
          <div>
            <span className="agreement-approval-detail__label">Проект</span>
            <div>{agreement?.project_name ?? "—"}</div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">
              Субподрядчик
            </span>
            <div>{counterpartyName}</div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">
              Тип исполнителя
            </span>
            <div>{performerTypeLabel ?? "—"}</div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">Сумма</span>
            <div>{formatCurrency(agreement?.price)}</div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">
              Период работ
            </span>
            <div>
              {agreement?.date_from ?? "—"} — {agreement?.date_to ?? "—"}
            </div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">
              Дата отправки
            </span>
            <div>{formatDateTime(approval?.created_at)}</div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">Блоки</span>
            <div>
              {blockRows.length > 0
                ? blockRows.map((block) => block.name).join(", ")
                : "—"}
            </div>
          </div>
          <div>
            <span className="agreement-approval-detail__label">Разделы</span>
            <div>
              {sectionLabels.length > 0 ? sectionLabels.join(", ") : "—"}
            </div>
          </div>
          {approval?.comment?.trim() && (
            <div className="agreement-approval-detail__comment-block">
              <span className="agreement-approval-detail__label">
                Комментарий при отправке
              </span>
              <div>{approval.comment}</div>
            </div>
          )}
        </div>

        {paymentStages.length > 0 && (
          <div className="agreement-approval-detail__payment-schedule">
            <span className="agreement-approval-detail__label">
              График оплат
            </span>
            <div className="agreement-approval-detail__payment-stages">
              {paymentStages.map((stage) => (
                <div
                  key={stage.key}
                  className="agreement-approval-detail__payment-stage"
                >
                  <span className="agreement-approval-detail__payment-stage-label">
                    {stage.label}
                  </span>
                  <span className="agreement-approval-detail__payment-stage-percent">
                    {stage.percent}%
                  </span>
                  <span className="agreement-approval-detail__payment-stage-amount">
                    {formatCurrency(stage.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {agreement?.id != null && (
          <button
            type="button"
            className="agreement-approval-detail__document-link"
            disabled={isExporting}
            onClick={async () => {
              if (isExporting) return;
              setIsExporting(true);
              setError(null);
              try {
                await exportAgreementXlsx(agreement.id, {
                  number: agreement.number,
                });
              } catch (err) {
                setError(err?.message || "Не удалось скачать соглашение");
              } finally {
                setIsExporting(false);
              }
            }}
          >
            {isExporting
              ? "Скачивание документа..."
              : "📄 Скачать документ соглашения"}
          </button>
        )}
      </div>

      <section className="agreement-approval-detail__section">
        <h3 className="agreement-approval-detail__section-title">
          Шаги согласования
        </h3>
        <div className="agreement-approval-detail__steps">
          {[...steps]
            .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
            .map((step) => (
              <div
                key={step.id}
                className={`agreement-approval-detail__step agreement-approval-detail__step--${step.status ?? "pending"}`}
              >
                <div className="agreement-approval-detail__step-main">
                  <span className="agreement-approval-detail__step-order">
                    {step.step_order}
                  </span>
                  <span className="agreement-approval-detail__step-name">
                    {step.approver_name ?? `ID ${step.approver_id}`}
                  </span>
                  <span
                    className={`agreement-approval-detail__step-status agreement-approval-detail__step-status--${step.status ?? "pending"}`}
                  >
                    {APPROVAL_STEP_STATUS_LABELS[step.status] ??
                      step.status ??
                      "—"}
                  </span>
                </div>
                {step.comment?.trim() && (
                  <div className="agreement-approval-detail__step-comment">
                    {step.comment}
                  </div>
                )}
                {step.decided_at && (
                  <div className="agreement-approval-detail__step-date">
                    {formatDateTime(step.decided_at)}
                  </div>
                )}
              </div>
            ))}
        </div>
      </section>

      {canDecide && (
        <div className="agreement-approval-detail__actions">
          {!showRejectForm ? (
            <>
              <button
                type="button"
                className="agreement-approval-detail__btn agreement-approval-detail__btn--reject"
                onClick={() => setShowRejectForm(true)}
                disabled={isProcessing}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="agreement-approval-detail__btn agreement-approval-detail__btn--approve"
                onClick={handleApprove}
                disabled={isProcessing}
              >
                {isProcessing ? "Обработка..." : "Согласовать"}
              </button>
            </>
          ) : (
            <div className="agreement-approval-detail__reject-form">
              <label className="agreement-approval-detail__label">
                Комментарий к отклонению
              </label>
              <textarea
                className="agreement-approval-detail__textarea"
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                rows={3}
                placeholder="Укажите причину отклонения"
                disabled={isProcessing}
              />
              <div className="agreement-approval-detail__reject-actions">
                <button
                  type="button"
                  className="agreement-approval-detail__btn agreement-approval-detail__btn--secondary"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectComment("");
                  }}
                  disabled={isProcessing}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="agreement-approval-detail__btn agreement-approval-detail__btn--reject"
                  onClick={handleReject}
                  disabled={isProcessing}
                >
                  {isProcessing ? "Отклонение..." : "Подтвердить отклонение"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="agreement-approval-detail__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default AgreementApprovalDetail;
